import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Building2,
  LayoutGrid,
  List,
  Search,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  Download,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { VendorsPortfolioBi } from '@/components/vendors/VendorsPortfolioBi';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { AddVendorModal } from '@/features/vendors/AddVendorModal';
import { PortfolioExportModal } from '@/features/vendors/PortfolioExportModal';
import { useVendors, useVendorCertStatus } from '@/api/hooks/useVendors';
import { useAuthStore } from '@/store/auth.store';
import { formatDate } from '@/lib/utils';
import type { Vendor, VendorTier, VendorFilters, VendorListMeta } from '@/api/types/vendor.types';
import { TIER_SHORT_LABELS } from '@/api/types/vendor.types';

// ── Filter panel ──────────────────────────────────────────────────────────────

const TIER_OPTIONS: { value: VendorTier | ''; label: string }[] = [
  { value: '', label: 'All tiers' },
  { value: 'tier_1_critical', label: 'Tier 1 — Critical' },
  { value: 'tier_2_high', label: 'Tier 2 — High' },
  { value: 'tier_3_medium', label: 'Tier 3 — Medium' },
  { value: 'tier_4_low', label: 'Tier 4 — Low' },
];

// BI segment filters that live in the URL + render as removable chips (Slice 3).
// ⚠ The composite score-band filter was REMOVED (it filtered on the non-live composite_risk_score).
const SEGMENT_FILTER_KEYS = ['tier', 'tiers', 'status', 'overdue', 'has_findings', 'finding_severity', 'cert_expiring', 'ra_open', 'outcome'] as const;
function filterChipLabel(key: string, value: string): string {
  switch (key) {
    case 'tier': return `Tier · ${TIER_SHORT_LABELS[value as VendorTier] ?? value}`;
    case 'tiers': return `Tier · ${value.split(',').map((t) => TIER_SHORT_LABELS[t as VendorTier] ?? t).join(' / ')}`;
    case 'status': return `Status · ${value.replace(/_/g, ' ')}`;
    case 'overdue': return 'Overdue assessment';
    case 'has_findings': return 'Has open findings';
    case 'finding_severity': return `${value.charAt(0).toUpperCase()}${value.slice(1)} findings`;
    case 'cert_expiring': return 'Cert expiring ≤90d';
    case 'ra_open': return 'Open risk response';
    case 'outcome': return `Outcome · ${value}`;
    default: return `${key} · ${value}`;
  }
}

function FilterPanel({ tier, onTier }: { tier: string; onTier: (t: string) => void }) {
  return (
    <div className="flex flex-wrap gap-3 rounded-lg border border-border-subtle bg-bg-surface p-3">
      <div className="flex flex-col gap-1">
        <label className="text-xs text-text-secondary">Tier</label>
        <select
          value={tier}
          onChange={(e) => onTier(e.target.value)}
          className="h-8 rounded border border-border-subtle bg-bg-surface-2 px-2 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
        >
          {TIER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </div>
  );
}

// ── Cert status badge mapping ─────────────────────────────────────────────────

const CERT_STATUS_BADGE = {
  no_certification:     { label: 'None',      variant: 'muted' },
  valid_certification:  { label: 'Valid',      variant: 'emerald' },
  expiring_soon:        { label: 'Expiring',   variant: 'amber' },
  expired_certification:{ label: 'Expired',    variant: 'rose' },
  withdrawn_certification:{ label: 'Withdrawn', variant: 'muted' },
  multiple_certifications:{ label: 'Multiple', variant: 'sky' },
} as const;

// ── Table row ─────────────────────────────────────────────────────────────────

function VendorRow({ vendor, retired = false }: { vendor: Vendor; retired?: boolean }) {
  const navigate = useNavigate();
  const { data: certStatus } = useVendorCertStatus(vendor.id);
  const certBadge = certStatus ? CERT_STATUS_BADGE[certStatus.status] : null;

  return (
    <tr
      onClick={() => navigate(`/vendors/${vendor.id}`)}
      // ⚠ D4 — retired rows read deliberately quieter than active ones (saturate down so status/cert pills
      //    lose their colour) so the two tables never look alike at a glance; active rows keep full colour.
      className={`cursor-pointer border-b border-border-subtle transition-colors hover:bg-bg-surface-2 ${retired ? 'saturate-[.45]' : ''}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${retired ? 'bg-bg-surface-2 text-text-secondary' : 'bg-accent-cyan-dim text-accent-cyan'}`}>
            {vendor.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className={`text-sm font-medium ${retired ? 'text-text-secondary' : 'text-text-primary'}`}>{vendor.name}</p>
            {vendor.industry && <p className="text-xs text-text-secondary">{vendor.industry}</p>}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <Badge variant="muted">{TIER_SHORT_LABELS[vendor.tier] ?? vendor.tier}</Badge>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={vendor.status} />
      </td>
      {/* ⚠ Retired table omits Last Assessment — the "Overdue N days" cell is misleading on offboarded rows. */}
      {!retired && (
        <td className="px-4 py-3 text-sm">
          {vendor.earliest_overdue_due ? (
            <span className="font-medium text-cert-expired-fg">
              Overdue {Math.max(1, Math.round((Date.now() - new Date(vendor.earliest_overdue_due).getTime()) / 86_400_000))} days
            </span>
          ) : vendor.last_assessed_at ? (
            <span className="text-text-secondary">{formatDistanceToNow(new Date(vendor.last_assessed_at), { addSuffix: true })}</span>
          ) : (
            <span className="text-text-secondary">—</span>
          )}
        </td>
      )}
      <td className="px-4 py-3">
        {/* ⚠ D4 — retired rows show "—" for findings: an offboarded vendor has no live remediation queue,
            so a live-looking number would mislead. Active rows show the real open-findings count. */}
        {retired ? (
          <span className="text-xs text-text-secondary" title="Offboarded — no live remediation queue">—</span>
        ) : (vendor.open_findings_count ?? 0) > 0 ? (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium tabular-nums text-text-primary">{vendor.open_findings_count}</span>
            {(vendor.open_findings_critical ?? 0) > 0 && (
              <span className="text-[11px] font-semibold text-severity-critical">· {vendor.open_findings_critical} crit</span>
            )}
          </div>
        ) : (
          <span className="text-xs text-text-secondary">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        {certStatus?.status === 'multiple_certifications' ? (
          <div className="relative group inline-flex">
            <Badge variant="sky">Multiple</Badge>
            <div className="absolute bottom-full left-0 mb-1.5 hidden group-hover:block z-50 w-44 rounded-md border border-border-subtle bg-bg-surface px-2.5 py-2 text-xs text-text-primary shadow-lg whitespace-nowrap">
              <p>Active: {certStatus.detail.active_count}</p>
              <p>Expiring: {certStatus.detail.expiring_count}</p>
              <p>Expired: {certStatus.detail.expired_count}</p>
              <p>Withdrawn: {certStatus.detail.withdrawn_count}</p>
            </div>
          </div>
        ) : certBadge ? (
          <Badge variant={certBadge.variant}>{certBadge.label}</Badge>
        ) : (
          <span className="text-xs text-text-secondary">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" className={`h-7 text-xs ${retired ? 'text-text-secondary' : ''}`} onClick={() => navigate(`/vendors/${vendor.id}`)}>
            View
          </Button>
        </div>
      </td>
      {/* ⚠ Retired On (far right, retired table only) — audit vendor.retired occurred_at; NULL → "—" with a
          "no offboarding audit record" title (never an inferred date). */}
      {retired && (
        <td className="px-4 py-3 text-sm text-text-secondary tabular-nums" title={vendor.retired_at ? undefined : 'No offboarding audit record'}>
          {vendor.retired_at ? formatDate(vendor.retired_at) : '—'}
        </td>
      )}
    </tr>
  );
}

// ── Vendor table (shared between active + retired sections) ───────────────────

// Active table shows Last Assessment; the retired table drops it and appends Retired Date (far right).
const ACTIVE_HEADERS = ['Vendor', 'Tier', 'Status', 'Last Assessment', 'Findings', 'Cert Status', 'Actions'];
const RETIRED_HEADERS = ['Vendor', 'Tier', 'Status', 'Findings', 'Cert Status', 'Actions', 'Retired On'];

function VendorTable({ vendors, isLoading, emptyLabel, retired = false }: {
  vendors: Vendor[];
  isLoading: boolean;
  emptyLabel: string;
  retired?: boolean;
}) {
  const headers = retired ? RETIRED_HEADERS : ACTIVE_HEADERS;
  return (
    <div className="rounded-lg border border-border-subtle overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-bg-surface border-b border-border-subtle">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? [...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-border-subtle">
                  {headers.map((h) => (
                    <td key={h} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>
                  ))}
                </tr>
              ))
            : vendors.map((v) => <VendorRow key={v.id} vendor={v} retired={retired} />)
          }
        </tbody>
      </table>
      {!isLoading && vendors.length === 0 && (
        <p className="px-4 py-6 text-sm text-text-secondary text-center">{emptyLabel}</p>
      )}
    </div>
  );
}

// ── Card view ─────────────────────────────────────────────────────────────────

function VendorCard({ vendor }: { vendor: Vendor }) {
  const navigate = useNavigate();
  return (
    <Card
      onClick={() => navigate(`/vendors/${vendor.id}`)}
      className="cursor-pointer transition-colors hover:border-accent-cyan/40 hover:bg-bg-surface-2"
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-cyan-dim text-sm font-bold text-accent-cyan">
              {vendor.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-text-primary">{vendor.name}</p>
              <p className="text-xs text-text-secondary">{vendor.industry ?? 'No industry'}</p>
            </div>
          </div>
          <Badge variant="muted" className="shrink-0 text-[10px]">
            {TIER_SHORT_LABELS[vendor.tier] ?? vendor.tier}
          </Badge>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <StatusBadge status={vendor.status} />
          <div className="text-right">
            <p className="text-xs text-text-secondary">
              {vendor.earliest_overdue_due
                ? <span className="font-medium text-cert-expired-fg">Overdue</span>
                : vendor.last_assessed_at ? `Assessed ${formatDate(vendor.last_assessed_at)}` : 'Never assessed'}
            </p>
            {(vendor.open_findings_count ?? 0) > 0 && (
              <p className="mt-1 text-[11px] text-text-secondary">
                {vendor.open_findings_count} findings{(vendor.open_findings_critical ?? 0) > 0 ? ` · ${vendor.open_findings_critical} crit` : ''}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Retired vendors collapsible section ───────────────────────────────────────

// Page-size selector (10/25/50/100). ⚠ No "All" — virtualization is DEFERRED, so the set is capped at 100.
const PAGE_SIZES = [10, 25, 50, 100];
function PageSizeSelect({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-text-secondary">Show</span>
      {PAGE_SIZES.map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className={`text-[11px] font-semibold rounded px-1.5 py-0.5 transition-colors ${value === n ? 'bg-accent-cyan/15 text-accent-cyan' : 'text-text-secondary hover:text-text-primary'}`}>
          {n}
        </button>
      ))}
    </div>
  );
}

function SectionPager({ page, setPage, meta, loading, noun }: { page: number; setPage: (n: number) => void; meta?: VendorListMeta; loading: boolean; noun: string }) {
  if (loading || !meta || meta.total === 0) return null;
  const totalPages = meta.total_pages ?? 1;
  const shown = Math.min(meta.per_page * page, meta.total);
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-text-secondary">Showing {shown} of {meta.total} {noun}</span>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" />Prev</Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next<ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}

function RetiredVendorsSection({ vendors, isLoading, meta, page, setPage, size, setSize }: {
  vendors: Vendor[]; isLoading: boolean; meta?: VendorListMeta; page: number; setPage: (n: number) => void; size: number; setSize: (n: number) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!isLoading && (meta?.total ?? 0) === 0) return null;

  return (
    <div className="space-y-3">
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-lg border border-border-subtle bg-bg-surface px-4 py-3 text-left hover:bg-bg-surface-2 transition-colors"
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text-primary">Retired Vendors</span>
          {meta && <Badge variant="muted" className="font-mono text-xs">{meta.total}</Badge>}
          <span className="text-[11px] text-text-secondary">own context · excluded from every active metric above</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-text-secondary" /> : <ChevronDown className="h-4 w-4 text-text-secondary" />}
      </button>

      {open && (
        <>
          <div className="flex justify-end"><PageSizeSelect value={size} onChange={(n) => { setSize(n); setPage(1); }} /></div>
          <div className="max-h-[420px] overflow-y-auto">
            <VendorTable vendors={vendors} isLoading={isLoading} emptyLabel="No retired vendors" retired />
          </div>
          <SectionPager page={page} setPage={setPage} meta={meta} loading={isLoading} noun="retired vendors" />
        </>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function VendorsPage() {
  const { hasAnyRole } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<'table' | 'grid'>('table');
  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [showFilters, setShowFilters] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [activeSize, setActiveSize] = useState(25);
  const [retiredPage, setRetiredPage] = useState(1);
  const [retiredSize, setRetiredSize] = useState(10);

  const canAdd = hasAnyRole(['risk_manager', 'ciso', 'super_admin']);

  // URL = the source of truth for BI segment filters (shareable/bookmarkable — Design #4). Point-in-
  // time viz KPIs stay current; drill-through sets these params, which drive the ACTIVE table + the viz.
  const setFilter = useCallback((key: string, value: string | null) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value == null || value === '') p.delete(key); else p.set(key, value);
      return p;
    });
    setActivePage(1);
  }, [setSearchParams]);

  const clearAll = useCallback(() => {
    setSearchParams(new URLSearchParams());
    setSearch('');
    setActivePage(1);
  }, [setSearchParams]);

  // ⚠ Item 2 — search commits on Enter / Search-click (explicit), NOT per keystroke. (Vendors previously
  // auto-committed via a 300ms debounce — it did NOT have the Assessments URL-value swallow bug, since its
  // input is local `search` state; this aligns it to the same explicit-submit UX as the fixed Assessments.)
  const submitSearch = useCallback(() => setFilter('search', search.trim() || null), [setFilter, search]);

  const vfilters: VendorFilters = {};
  for (const k of ['tier', 'tiers', 'status', 'overdue', 'has_findings', 'finding_severity', 'cert_expiring', 'ra_open', 'outcome', 'search'] as const) {
    const val = searchParams.get(k);
    if (val) vfilters[k] = val;
  }

  // ⚠ Two-fetch split — TRUE full-population counts (not per-page): active (status<>offboarded, the
  // segment filters apply) + retired (status=offboarded, its OWN independent context — Design #2).
  const { data: activeData, isLoading: activeLoading } = useVendors({ filters: { ...vfilters, active_only: 'true' }, page: activePage, perPage: activeSize });
  const { data: retiredData, isLoading: retiredLoading } = useVendors({ filters: { status: 'offboarded' }, page: retiredPage, perPage: retiredSize });

  const activeVendors = activeData?.vendors ?? [];
  const activeMeta = activeData?.meta;
  const retiredVendors = retiredData?.vendors ?? [];
  const retiredMeta = retiredData?.meta;

  const activeChips = SEGMENT_FILTER_KEYS
    .map((k) => [k as string, searchParams.get(k)] as [string, string | null])
    .filter((e): e is [string, string] => e[1] != null);


  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-text-primary">Vendors</h1>
          {activeMeta && (
            <Badge variant="muted" className="font-mono">{activeMeta.total}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setExportOpen(true)}>
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Button
            variant={view === 'table' ? 'secondary' : 'ghost'}
            size="icon-sm"
            onClick={() => setView('table')}
            aria-label="Table view"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={view === 'grid' ? 'secondary' : 'ghost'}
            size="icon-sm"
            onClick={() => setView('grid')}
            aria-label="Card grid view"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          {canAdd && (
            <Button onClick={() => setAddOpen(true)}>
              <Building2 className="h-4 w-4" />
              Add Vendor
            </Button>
          )}
        </div>
      </div>

      {/* ⚠ D6 — search + composable filter chips at the TOP (MI), above the BI workspace. The active filter
          chips render INSIDE the search field and the field border lights while any filter is active.
          Composable is KEPT (each facet is its own URL key — same-facet REPLACES, cross-facet COMBINES);
          N individually-removable chips + Clear all; attention rows still NAVIGATE, not filter. */}
      <div className="flex items-center gap-2 w-full md:max-w-[52%]">
        <div className={`flex-1 min-w-0 flex items-center gap-1.5 flex-wrap rounded-md border bg-bg-surface px-2.5 py-1.5 transition-colors ${activeChips.length > 0 ? 'border-accent-cyan/60 ring-1 ring-accent-cyan/25' : 'border-border-subtle'}`}>
          <Search className="h-4 w-4 shrink-0 text-text-secondary" />
          {activeChips.map(([key, value]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key, null)}
              className="inline-flex items-center gap-1 rounded border border-accent-cyan/40 bg-accent-cyan/10 px-1.5 py-0.5 text-[11px] font-medium text-accent-cyan hover:bg-accent-cyan/20 transition-colors"
            >
              {filterChipLabel(key, value)}
              <X className="h-3 w-3" />
            </button>
          ))}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
            placeholder={activeChips.length > 0 ? 'Add a name search…' : 'Search vendors by name, domain, industry…'}
            className="flex-1 min-w-[120px] bg-transparent text-sm text-text-primary placeholder:text-text-secondary focus:outline-none py-0.5"
          />
          {activeChips.length > 0 && (
            <button type="button" onClick={clearAll} className="shrink-0 text-[11px] text-text-secondary hover:text-text-primary underline">Clear all</button>
          )}
        </div>
        <Button size="sm" onClick={submitSearch}>Search</Button>
        <Button
          variant={showFilters ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {activeChips.length > 0 && (
            <Badge variant="default" className="ml-1 text-[10px] py-0 px-1.5 tabular-nums">{activeChips.length}</Badge>
          )}
        </Button>
      </div>

      {showFilters && (
        <FilterPanel tier={searchParams.get('tier') ?? ''} onTier={(t) => setFilter('tier', t || null)} />
      )}

      {/* Portfolio intelligence — BI workspace (KPIs + trends + attention) below the search/filter bar */}
      <VendorsPortfolioBi onSegment={setFilter} />

      {/* Table view — two sections */}
      {view === 'table' && (
        <div className="space-y-6">
          {/* Active Vendors — the segment filters apply here (true full-population count) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-text-primary">Active Vendors</h2>
                {activeMeta && <Badge variant="muted" className="font-mono text-xs">{activeMeta.total}</Badge>}
              </div>
              <PageSizeSelect value={activeSize} onChange={(n) => { setActiveSize(n); setActivePage(1); }} />
            </div>
            <div className="max-h-[520px] overflow-y-auto">
              <VendorTable vendors={activeVendors} isLoading={activeLoading}
                emptyLabel={activeChips.length > 0 ? `No vendors match these ${activeChips.length} filter${activeChips.length > 1 ? 's' : ''} — the chips above stay set so you can remove one or Clear all.` : 'No active vendors.'} />
            </div>
            <SectionPager page={activePage} setPage={setActivePage} meta={activeMeta} loading={activeLoading} noun="active vendors" />
          </div>

          {/* Retired Vendors — own independent context (own page-size, no segment filters) */}
          <RetiredVendorsSection
            vendors={retiredVendors} isLoading={retiredLoading} meta={retiredMeta}
            page={retiredPage} setPage={setRetiredPage} size={retiredSize} setSize={setRetiredSize}
          />
        </div>
      )}

      {/* Grid view */}
      {view === 'grid' && (
        <>
          {(activeLoading || retiredLoading) ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <Card key={i}><CardContent className="p-4"><Skeleton className="h-32 w-full" /></CardContent></Card>
              ))}
            </div>
          ) : activeVendors.length === 0 && retiredVendors.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="No vendors yet"
              description="Add your first vendor to start managing third-party risk."
              action={canAdd ? { label: 'Add your first vendor', onClick: () => setAddOpen(true) } : undefined}
            />
          ) : (
            <div className="space-y-6">
              {activeVendors.length > 0 && (
                <div className="space-y-3">
                  <h2 className="text-sm font-semibold text-text-primary">Active Vendors</h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {activeVendors.map((v) => <VendorCard key={v.id} vendor={v} />)}
                  </div>
                </div>
              )}
              {retiredVendors.length > 0 && (
                <div className="space-y-3">
                  <h2 className="text-sm font-semibold text-text-secondary">Retired Vendors</h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {retiredVendors.map((v) => <VendorCard key={v.id} vendor={v} />)}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <AddVendorModal open={addOpen} onClose={() => setAddOpen(false)} />
      <PortfolioExportModal open={exportOpen} onClose={() => setExportOpen(false)} currentParams={searchParams} range="90d" />
    </div>
  );
}
